import { matchesEveryWord } from '@cpce/shared';
import { Command as CommandPrimitive } from 'cmdk';
import { Check } from 'lucide-react';
import { type KeyboardEvent, useCallback, useRef, useState } from 'react';
import { cn } from '@/utils/style';
import { Input } from './Input';
import { Skeleton } from './Skeleton';
import { CommandGroup, CommandItem, CommandList } from './shadcn/Command';

export type AutoCompleteOption<T> = {
  id: string | number;
  label: string;
  value: T;
};

export type AutoCompleteProps<T> = {
  options: AutoCompleteOption<T>[];
  emptyMessage?: string;
  value?: string;
  onChange?: (value: string) => void;
  onSelect?: (value: T) => void;
  isLoading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  icon?: React.ReactNode;
  clearOnSelect?: boolean;
  className?: string;
};

export function AutoComplete<T>(props: AutoCompleteProps<T>) {
  const {
    options,
    placeholder,
    emptyMessage = 'Aucun résultat trouvé',
    value,
    onChange,
    onSelect,
    disabled,
    isLoading = false,
    icon,
    clearOnSelect,
    className,
  } = props;
  const inputRef = useRef<HTMLInputElement>(null);

  const [isOpen, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState<string>(value || '');

  const filteredOptions = options.filter((option) => matchesEveryWord(option.label, inputValue));

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      const input = inputRef.current;
      if (!input) return;

      if (!isOpen) setOpen(true);

      if (event.key === 'Escape') input.blur();
    },
    [isOpen]
  );

  const handleBlur = useCallback(() => {
    setOpen(false);
    setInputValue(inputValue);
  }, [inputValue]);

  const handleSelectOption = useCallback(
    (selectedOption: AutoCompleteOption<T>) => {
      setInputValue(selectedOption.label);
      onSelect?.(selectedOption.value);
      onChange?.(clearOnSelect ? '' : selectedOption.label);
      if (clearOnSelect) setInputValue('');
    },
    [onChange, onSelect, clearOnSelect]
  );

  return (
    <CommandPrimitive shouldFilter={false} className={className}>
      <Input
        ref={inputRef}
        onKeyDown={handleKeyDown}
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          onChange?.(e.target.value);
        }}
        onBlur={handleBlur}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        disabled={disabled}
        icon={icon}
      />
      <div className="relative mt-1">
        <div
          className={cn(
            'animate-in fade-in-0 zoom-in-95 absolute top-0 z-10 w-full rounded-xl bg-white outline-none',
            isOpen ? 'block' : 'hidden'
          )}
        >
          <CommandList className="rounded-lg ring-1 ring-slate-200">
            {isLoading ? (
              <CommandPrimitive.Loading>
                <div className="p-1">
                  <Skeleton className="h-8 w-full" />
                </div>
              </CommandPrimitive.Loading>
            ) : null}
            {filteredOptions.length > 0 && !isLoading ? (
              <CommandGroup>
                {filteredOptions.map((option) => {
                  const isSelected = inputValue === option.label;
                  return (
                    <CommandItem
                      key={option.id}
                      value={String(option.id)}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                      onSelect={() => handleSelectOption(option)}
                      className={cn(
                        'flex w-full items-center gap-2',
                        !isSelected ? 'pl-8' : null,
                        'aria-selected:bg-gray-100 data-[selected=true]:bg-gray-100'
                      )}
                    >
                      {isSelected ? <Check className="w-4" /> : null}
                      {option.label}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ) : null}
            {!isLoading ? (
              <CommandPrimitive.Empty className="select-none rounded-sm px-2 py-3 text-center text-sm">
                {emptyMessage}
              </CommandPrimitive.Empty>
            ) : null}
          </CommandList>
        </div>
      </div>
    </CommandPrimitive>
  );
}
